import { useState, useEffect, useCallback } from 'react';
import {
  getFeedStatus,
  uploadFeedFile,
  previewFile,
  deactivateFeed,
} from '../services/api/referenceDataApi';

/**
 * Hook for managing reference data feeds.
 * Fetches feed status for a customer, handles file upload/preview, and feed deactivation.
 */
export const useReferenceData = (customerId) => {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchFeeds = useCallback(async () => {
    if (!customerId) {
      setFeeds([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await getFeedStatus(customerId);
      setFeeds(response.data?.feeds || []);
    } catch (err) {
      let errorMessage = 'Failed to fetch reference data feeds';
      if (err.response?.status === 401) {
        errorMessage = 'You do not have permission to view reference data feeds.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Access denied. You need the Augmentation Administrator role.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      setError(errorMessage);
      console.error('Error fetching feeds:', err);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  // Fetch on mount and when customerId changes
  useEffect(() => {
    fetchFeeds();
  }, [fetchFeeds]);

  const uploadFile = async (feedId, file) => {
    try {
      setError(null);
      const response = await uploadFeedFile(feedId, file);
      setSuccess(`File "${file.name}" uploaded successfully. Ingestion pipeline will process it automatically.`);
      setTimeout(() => setSuccess(null), 5000);
      // Refresh feeds to show updated status after a short delay (ingestion is async)
      setTimeout(() => fetchFeeds(), 3000);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data || 'Failed to upload file';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      throw err;
    }
  };

  const previewUploadFile = async (file) => {
    try {
      setError(null);
      const response = await previewFile(file);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data || 'Failed to preview file';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      throw err;
    }
  };

  const deactivate = async (feedId) => {
    try {
      setError(null);
      const response = await deactivateFeed(feedId);
      setSuccess(response.data?.message || 'Feed deactivated successfully.');
      setTimeout(() => setSuccess(null), 5000);
      await fetchFeeds();
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data || 'Failed to deactivate feed';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      throw err;
    }
  };

  return {
    feeds,
    loading,
    error,
    success,
    fetchFeeds,
    uploadFile,
    previewUploadFile,
    deactivate,
  };
};
